import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spin, Tabs } from "antd";
import {
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  WalletOutlined,
  TransactionOutlined,
  BarChartOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const { TabPane } = Tabs;

export default function OrganisationDetails() {
  const { organisationId } = useParams();
  const navigate = useNavigate();
  const [organisationDetails, setOrganisationDetails] = useState(null);
  const [subOrganisations, setSubOrganisations] = useState([]);
  const [provisionRequests, setProvisionRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subOrgLoading, setSubOrgLoading] = useState(false);
  const [provisionLoading, setProvisionLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [transactions, setTransactions] = useState([]);
  const [loadTransactions, setLoadTransactions] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 10;

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  useEffect(() => {
    const fetchOrganisationDetails = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/organisations/${organisationId}`,
        );

        if (!res.ok) {
          throw new Error("Organisation introuvable");
        }

        const data = await res.json();
        setOrganisationDetails(data);

        if (data.organisationType === "main") {
          fetchSubOrganisations();
        }

        fetchProvisionRequests();
      } catch (error) {
        console.error("Erreur :", error.message);
        showMessage("Erreur lors du chargement des détails", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchOrganisationDetails();
    fetchTransactions();
  }, [organisationId]);

  const fetchSubOrganisations = async () => {
    try {
      setSubOrgLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organisations/${organisationId}/sub-organisations`,
      );

      if (res.ok) {
        const subOrgs = await res.json();
        setSubOrganisations(subOrgs);
      }
    } catch (error) {
      console.warn("Erreur lors du chargement des sous-organisations");
    } finally {
      setSubOrgLoading(false);
    }
  };

  const fetchProvisionRequests = async () => {
    try {
      setProvisionLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/provision_requests`,
      );

      if (res.ok) {
        const requests = await res.json();
        const filteredRequests = requests.filter(
          (req) => req.organisationId === organisationId,
        );
        setProvisionRequests(filteredRequests);
      }
    } catch (error) {
      console.warn(
        "Erreur lors du chargement des demandes d'approvisionnement",
      );
    } finally {
      setProvisionLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoadTransactions(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organisations/${organisationId}/transactions`,
      );

      if (res.ok) {
        const data = await res.json();
        const formatted = data.map((t) => {
          if (t.paymentDetails && typeof t.paymentDetails === "string") {
            try {
              t.paymentDetails = JSON.parse(t.paymentDetails);
            } catch (e) {
              console.warn("Erreur parsing paymentDetails", e);
            }
          }
          return t;
        });
        setTransactions(formatted);
      }
    } catch (error) {
      console.warn("Erreur lors du chargement des transactions");
    } finally {
      setLoadTransactions(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("fr-FR").format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return { bg: "#f6ffed", color: "#52c41a" };
      case "rejected":
        return { bg: "#fff2f0", color: "#ff4d4f" };
      case "pending":
        return { bg: "#fff7e6", color: "#fa8c16" };
      default:
        return { bg: "#f0f0f0", color: "#666" };
    }
  };

  const getProviderImage = (transaction) => {
    if (transaction.paymentDetails?.service?.toLowerCase() === "credit-card") {
      return (
        <img
          src="/v-c.webp"
          alt="credit-card"
          style={{ height: "32px", width: "32px", objectFit: "fill" }}
        />
      );
    }

    const provider =
      transaction.paymentDetails?.provider ||
      transaction.details?.senderDetails?.provider;

    const providerImages = {
      wave: "/wave.png",
      orange: "/orange.jpg",
      mtn: "/mtn.png",
      moov: "/moov.png",
      air: "/air.png",
    };

    if (provider && providerImages[provider]) {
      return (
        <img
          src={providerImages[provider]}
          alt={provider}
          style={{ height: "32px", width: "32px" }}
        />
      );
    }

    if (transaction.transactionType === "airtime") {
      return (
        <img
          src="/air.png"
          alt="airtime"
          style={{ height: "32px", width: "32px" }}
        />
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Composant pour les informations générales
  const GeneralInfoTab = () => (
    <div>
      {/* Informations de l'organisation */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          marginBottom: "20px",
        }}
      >
        <div style={{ padding: "20px" }}>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "20px",
              color: "#1890ff",
            }}
          >
            Informations générales
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "20px",
            }}
          >
            <div>
              <strong>Nom de l'organisation :</strong>
              <div style={{ marginTop: "5px" }}>
                {organisationDetails.name || "N/A"}
              </div>
            </div>
            <div>
              <strong>ID Organisation :</strong>
              <div
                style={{
                  marginTop: "5px",
                  fontFamily: "monospace",
                  backgroundColor: "#f5f5f5",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                }}
              >
                {organisationDetails.organisation_id || "N/A"}
              </div>
            </div>
            <div>
              <strong>Téléphone :</strong>
              <div style={{ marginTop: "5px" }}>
                +{organisationDetails.phone || "N/A"}
              </div>
            </div>
            <div>
              <strong>Type d'organisation :</strong>
              <div style={{ marginTop: "5px" }}>
                <span
                  style={{
                    padding: "4px 12px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor:
                      organisationDetails.organisation_type === "main"
                        ? "#e6f7ff"
                        : "#fff7e6",
                    color:
                      organisationDetails.organisation_type === "main"
                        ? "#1890ff"
                        : "#fa8c16",
                  }}
                >
                  {organisationDetails.organisation_type === "main"
                    ? "Principale"
                    : "Secondaire"}
                </span>
              </div>
            </div>
            <div>
              <strong>Type de compte :</strong>
              <div style={{ marginTop: "5px" }}>
                <span
                  style={{
                    padding: "4px 12px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor:
                      organisationDetails.account_type === "marchand"
                        ? "#e6f7ff"
                        : organisationDetails.account_type === "school"
                          ? "#fff7e6"
                          : organisationDetails.account_type === "pdv"
                            ? "#F0FFFF"
                            : organisationDetails.account_type === "enterprise"
                              ? "#DDA0DD"
                              : "#f5f5f5",
                    color:
                      organisationDetails.account_type === "marchand"
                        ? "#1890ff"
                        : organisationDetails.account_type === "school"
                          ? "#fa8c16"
                          : organisationDetails.account_type === "pdv"
                            ? "#20B2AA"
                            : organisationDetails.account_type === "enterprise"
                              ? "#9932CC"
                              : "#666",
                  }}
                >
                  {organisationDetails.account_type || "N/A"}
                </span>
              </div>
            </div>
            <div>
              <strong>Statut :</strong>
              <div style={{ marginTop: "5px" }}>
                <span
                  style={{
                    padding: "4px 12px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor:
                      organisationDetails.status === "active"
                        ? "#f6ffed"
                        : "#fff2f0",
                    color:
                      organisationDetails.status === "active"
                        ? "#52c41a"
                        : "#ff4d4f",
                  }}
                >
                  {organisationDetails.status === "active"
                    ? "Actif"
                    : "Inactif"}
                </span>
              </div>
            </div>
            <div>
              <strong>Date de création :</strong>
              <div style={{ marginTop: "5px" }}>
                {organisationDetails.created_at
                  ? new Date(organisationDetails.created_at).toLocaleString()
                  : "N/A"}
              </div>
            </div>
            <div>
              <strong>Date de mise à jour :</strong>
              <div style={{ marginTop: "5px" }}>
                {organisationDetails.updated_at
                  ? new Date(organisationDetails.updated_at).toLocaleString()
                  : "N/A"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informations du propriétaire */}
      {organisationDetails.owner && (
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            marginBottom: "20px",
          }}
        >
          <div style={{ padding: "20px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "20px",
                color: "#52c41a",
              }}
            >
              Informations du propriétaire
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "20px",
              }}
            >
              <div>
                <strong>Nom complet :</strong>
                <div style={{ marginTop: "5px" }}>
                  {organisationDetails.owner.fullname || "N/A"}
                </div>
              </div>
              <div>
                <strong>Email :</strong>
                <div style={{ marginTop: "5px" }}>
                  {organisationDetails.owner.email || "N/A"}
                </div>
              </div>
              <div>
                <strong>Téléphone :</strong>
                <div style={{ marginTop: "5px" }}>
                  +{organisationDetails.owner.phone || "N/A"}
                </div>
              </div>
              <div>
                <strong>Statut :</strong>
                <div style={{ marginTop: "5px" }}>
                  <span
                    style={{
                      padding: "4px 12px",
                      borderRadius: "4px",
                      fontSize: "14px",
                      backgroundColor:
                        organisationDetails.owner.status === "active"
                          ? "#f6ffed"
                          : "#fff2f0",
                      color:
                        organisationDetails.owner.status === "active"
                          ? "#52c41a"
                          : "#ff4d4f",
                    }}
                  >
                    {organisationDetails.owner.status === "active"
                      ? "Actif"
                      : "Inactif"}
                  </span>
                </div>
              </div>
              <div>
                <strong>ID Utilisateur :</strong>
                <div
                  style={{
                    marginTop: "5px",
                    fontFamily: "monospace",
                    backgroundColor: "#f5f5f5",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                  }}
                >
                  {organisationDetails.owner.userUuid || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Composant pour le wallet
  const WalletTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#fa8c16",
        }}
      >
        Informations du Wallet
      </h3>

      {organisationDetails.wallet ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px",
          }}
        >
          <div
            style={{
              padding: "15px",
              backgroundColor: "#f6ffed",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
            >
              Balance
            </div>
            <div
              style={{ fontSize: "24px", fontWeight: "600", color: "#52c41a" }}
            >
              {formatAmount(organisationDetails.wallet.balance)} CFA
            </div>
          </div>
          <div
            style={{
              padding: "15px",
              backgroundColor: "#e6f7ff",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
            >
              Collect
            </div>
            <div
              style={{ fontSize: "24px", fontWeight: "600", color: "#1890ff" }}
            >
              {formatAmount(organisationDetails.wallet.collect)} CFA
            </div>
          </div>
          <div
            style={{
              padding: "15px",
              backgroundColor: "#fff7e6",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
            >
              Transfer
            </div>
            <div
              style={{ fontSize: "24px", fontWeight: "600", color: "#fa8c16" }}
            >
              {formatAmount(organisationDetails.wallet.transfer)} CFA
            </div>
          </div>
          <div
            style={{
              padding: "15px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
            >
              Statut
            </div>
            <span
              style={{
                padding: "4px 12px",
                borderRadius: "4px",
                fontSize: "14px",
                backgroundColor:
                  organisationDetails.wallet.statut === "active"
                    ? "#f6ffed"
                    : "#fff2f0",
                color:
                  organisationDetails.wallet.statut === "active"
                    ? "#52c41a"
                    : "#ff4d4f",
              }}
            >
              {organisationDetails.wallet.statut === "active"
                ? "Actif"
                : "Inactif"}
            </span>
          </div>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#666",
            backgroundColor: "#fafafa",
            borderRadius: "8px",
          }}
        >
          Aucune information de wallet disponible
        </div>
      )}
    </div>
  );

  // Composant pour les sous-organisations
  const SubOrganisationsTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#722ed1",
        }}
      >
        Sous-organisations ({subOrganisations.length})
      </h3>

      {subOrgLoading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      ) : subOrganisations.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ backgroundColor: "#fafafa" }}>
              <tr>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Nom
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Type de compte
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Statut
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Balance
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Numéro de l'organisation
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  QR Code
                </th>
              </tr>
            </thead>
            <tbody>
              {subOrganisations.map((subOrg) => (
                <tr
                  key={subOrg.organisationId}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  <td style={{ padding: "12px" }}>{subOrg.name}</td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor:
                          subOrg.accountType === "marchand"
                            ? "#e6f7ff"
                            : subOrg.accountType === "school"
                              ? "#fff7e6"
                              : subOrg.accountType === "pdv"
                                ? "#F0FFFF"
                                : subOrg.accountType === "enterprise"
                                  ? "#DDA0DD"
                                  : "#f5f5f5",
                        color:
                          subOrg.accountType === "marchand"
                            ? "#1890ff"
                            : subOrg.accountType === "school"
                              ? "#fa8c16"
                              : subOrg.accountType === "pdv"
                                ? "#20B2AA"
                                : subOrg.accountType === "enterprise"
                                  ? "#9932CC"
                                  : "#666",
                      }}
                    >
                      {subOrg.accountType}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor:
                          subOrg.status === "active" ? "#f6ffed" : "#fff2f0",
                        color:
                          subOrg.status === "active" ? "#52c41a" : "#ff4d4f",
                      }}
                    >
                      {subOrg.status === "active" ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    {formatAmount(subOrg.wallet?.balance || 0)} CFA
                  </td>
                  <td style={{ padding: "12px" }}>
                    {subOrg.qrCode ? (
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor:
                            subOrg.qrCode.status === "active"
                              ? "#f6ffed"
                              : "#fff2f0",
                          color:
                            subOrg.qrCode.status === "active"
                              ? "#52c41a"
                              : "#ff4d4f",
                        }}
                      >
                        {subOrg.phone}
                      </span>
                    ) : (
                      <span style={{ color: "#999" }}>N/A</span>
                    )}
                  </td>
                  <td style={{ padding: "12px" }}>
                    {subOrg.qrCode ? (
                      <a
                        href={subOrg.qrCode.url}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor:
                            subOrg.qrCode.status === "active"
                              ? "#f6ffed"
                              : "#fff2f0",
                          color:
                            subOrg.qrCode.status === "active"
                              ? "#52c41a"
                              : "#ff4d4f",
                          textDecoration: "none",
                        }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {subOrg.qrCode.url}
                      </a>
                    ) : (
                      <span style={{ color: "#999" }}>N/A</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#666",
            backgroundColor: "#fafafa",
            borderRadius: "8px",
          }}
        >
          Aucune sous-organisation trouvée
        </div>
      )}
    </div>
  );

  // Composant pour les demandes d'approvisionnement
  const ProvisionRequestsTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#f5222d",
        }}
      >
        Demandes d'approvisionnement ({provisionRequests.length})
      </h3>

      {provisionLoading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      ) : provisionRequests.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ backgroundColor: "#fafafa" }}>
              <tr>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Référence
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Montant
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Statut
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Type
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Fournisseur de paiement
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Date de création
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Document
                </th>
              </tr>
            </thead>
            <tbody>
              {provisionRequests.map((request) => {
                const statusColors = getStatusColor(request.status);
                return (
                  <tr
                    key={request.id}
                    style={{ borderBottom: "1px solid #f0f0f0" }}
                  >
                    <td
                      style={{
                        padding: "12px",
                        fontFamily: "monospace",
                        fontSize: "12px",
                      }}
                    >
                      {request.paymentProviderReference}
                    </td>
                    <td style={{ padding: "12px", fontWeight: "600" }}>
                      {formatAmount(request.amount)} CFA
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor: statusColors.bg,
                          color: statusColors.color,
                        }}
                      >
                        {request.status === "approved"
                          ? "Approuvé"
                          : request.status === "rejected"
                            ? "Rejeté"
                            : request.status === "pending"
                              ? "En attente"
                              : request.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor: "#f0f2f5",
                          color: "#666",
                        }}
                      >
                        {request.provisionType}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      {request.paymentProvider ? (
                        <div>
                          <div style={{ fontWeight: "500" }}>
                            {request.paymentProvider.label}
                          </div>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            {request.paymentProvider.number}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: "#999" }}>N/A</span>
                      )}
                    </td>
                    <td style={{ padding: "12px", fontSize: "12px" }}>
                      {new Date(request.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {request.documentUrl ? (
                        <a
                          href={request.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1890ff",
                            textDecoration: "none",
                            fontSize: "12px",
                          }}
                        >
                          📄 Voir
                        </a>
                      ) : (
                        <span style={{ color: "#999", fontSize: "12px" }}>
                          N/A
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#666",
            backgroundColor: "#fafafa",
            borderRadius: "8px",
          }}
        >
          Aucune demande d'approvisionnement trouvée
        </div>
      )}
    </div>
  );

  // Filtrer les transactions
  const filteredTransactions = transactions.filter((t) => {
    const search = searchTerm.toLowerCase();
    return t.reference.toLowerCase().includes(search);
  });

  // Pagination
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(
    indexOfFirstTransaction,
    indexOfLastTransaction,
  );

  const totalPages = Math.ceil(
    filteredTransactions.length / transactionsPerPage,
  );

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Composant pour les transactions
  const TransactionsTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#1890ff",
        }}
      >
        Transactions ({transactions.length})
      </h3>

      {/* Barre de recherche */}
      <div style={{ marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Rechercher par référence"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            padding: "8px 12px",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            width: "300px",
            fontSize: "14px",
          }}
        />
      </div>

      {loadTransactions ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      ) : filteredTransactions.length > 0 ? (
        <div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ backgroundColor: "#fafafa" }}>
                <tr>
                  <th style={{ padding: "12px", textAlign: "left" }}>
                    Référence
                  </th>
                  <th style={{ padding: "12px", textAlign: "left" }}>
                    Montant
                  </th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Frais</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Type</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Statut</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>
                    Fournisseur
                  </th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {currentTransactions.map((t) => (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "12px" }}>{t.reference}</td>
                    <td style={{ padding: "12px" }}>
                      {formatAmount(t.amount)} {t.currency}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {t.fees.amount} {t.currency}
                    </td>
                    <td style={{ padding: "12px" }}>{t.transactionType}</td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor:
                            t.status === "success"
                              ? "#f6ffed"
                              : t.status === "failed"
                                ? "#fff2f0"
                                : "#fff7e6",
                          color:
                            t.status === "success"
                              ? "#52c41a"
                              : t.status === "failed"
                                ? "#ff4d4f"
                                : "#faad14",
                        }}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>{getProviderImage(t)}</td>
                    <td style={{ padding: "12px" }}>
                      {new Date(t.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 0",
            }}
          >
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                backgroundColor: currentPage === 1 ? "#f5f5f5" : "white",
                color: currentPage === 1 ? "#999" : "#333",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Précédent
            </button>
            <span style={{ fontSize: "14px", color: "#666" }}>
              Page {currentPage} sur {totalPages} ({filteredTransactions.length}{" "}
              transactions)
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                backgroundColor:
                  currentPage === totalPages ? "#f5f5f5" : "white",
                color: currentPage === totalPages ? "#999" : "#333",
                borderRadius: "4px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
            >
              Suivant
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#666",
            backgroundColor: "#fafafa",
            borderRadius: "8px",
          }}
        >
          Aucune transaction trouvée
        </div>
      )}
    </div>
  );

  // Composant pour les statistiques
  const StatisticsTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#722ed1",
        }}
      >
        Statistiques des transactions
      </h3>

      {transactions.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "20px",
          }}
        >
          {/* Total transferts */}
          <div
            style={{
              padding: "15px",
              backgroundColor: "#e6f7ff",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "14px", color: "#666" }}>
              Total Transferts
            </div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#1890ff",
              }}
            >
              {formatAmount(
                transactions
                  .filter((t) => t.transactionType === "payout")
                  .reduce((sum, t) => sum + Number(t.amount || 0), 0),
              )}{" "}
              CFA
            </div>
          </div>

          {/* Total collectes */}
          <div
            style={{
              padding: "15px",
              backgroundColor: "#fff7e6",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "14px", color: "#666" }}>
              Total Collectes
            </div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#fa8c16",
              }}
            >
              {formatAmount(
                transactions
                  .filter((t) => t.transactionType === "withdrawal")
                  .reduce((sum, t) => sum + Number(t.amount || 0), 0),
              )}{" "}
              CFA
            </div>
          </div>

          {/* Total Airtime */}
          <div
            style={{
              padding: "15px",
              backgroundColor: "#f0f5ff",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "14px", color: "#666" }}>Total Airtime</div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#2f54eb",
              }}
            >
              {formatAmount(
                transactions
                  .filter((t) => t.transactionType === "airtime")
                  .reduce((sum, t) => sum + Number(t.amount || 0), 0),
              )}{" "}
              CFA
            </div>
          </div>

          {/* Total frais */}
          <div
            style={{
              padding: "15px",
              backgroundColor: "#f6ffed",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "14px", color: "#666" }}>
              Total Frais appliqués
            </div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#52c41a",
              }}
            >
              {formatAmount(
                transactions.reduce(
                  (sum, t) => sum + Number(t.fees?.amount || 0),
                  0,
                ),
              )}{" "}
              CFA
            </div>
          </div>

          {/* Total commissions */}
          <div
            style={{
              padding: "15px",
              backgroundColor: "#fff2f0",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "14px", color: "#666" }}>
              Total Commissions
            </div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#ff4d4f",
              }}
            >
              {formatAmount(
                transactions.reduce(
                  (sum, t) => sum + Number(t.commission?.amount || 0),
                  0,
                ),
              )}{" "}
              CFA
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#666",
            backgroundColor: "#fafafa",
            borderRadius: "8px",
          }}
        >
          Aucune statistique disponible
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      {/* Message d'alerte */}
      {message.text && (
        <div
          style={{
            padding: "10px",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: message.type === "error" ? "#ffebee" : "#e8f5e8",
            color: message.type === "error" ? "#c62828" : "#2e7d32",
            border: `1px solid ${
              message.type === "error" ? "#ef5350" : "#66bb6a"
            }`,
          }}
        >
          {message.text}
        </div>
      )}

      {/* Header avec bouton retour */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "28px", fontWeight: "600" }}>
          Détails de l'organisation
        </h1>
        <button
          onClick={() => navigate("/organisations")}
          style={{
            padding: "8px 16px",
            backgroundColor: "#f5f5f5",
            color: "#333",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          ← Retour à la liste
        </button>
      </div>

      {/* Tabs pour organiser le contenu */}
      <Tabs defaultActiveKey="1" type="card" size="large">
        <TabPane
          tab={
            <span>
              <UserOutlined />
              Informations générales
            </span>
          }
          key="1"
        >
          <GeneralInfoTab />
        </TabPane>

        <TabPane
          tab={
            <span>
              <WalletOutlined />
              Wallet
            </span>
          }
          key="2"
        >
          <WalletTab />
        </TabPane>

        {organisationDetails?.organisationType === "main" && (
          <TabPane
            tab={
              <span>
                <TeamOutlined />
                Sous-organisations ({subOrganisations.length})
              </span>
            }
            key="3"
          >
            <SubOrganisationsTab />
          </TabPane>
        )}

        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              Demandes d'approvisionnement ({provisionRequests.length})
            </span>
          }
          key="4"
        >
          <ProvisionRequestsTab />
        </TabPane>

        <TabPane
          tab={
            <span>
              <TransactionOutlined />
              Transactions ({transactions.length})
            </span>
          }
          key="5"
        >
          <TransactionsTab />
        </TabPane>

        <TabPane
          tab={
            <span>
              <BarChartOutlined />
              Statistiques
            </span>
          }
          key="6"
        >
          <StatisticsTab />
        </TabPane>
      </Tabs>
    </div>
  );
}
